import { action, computed, makeAutoObservable, runInAction } from "mobx"
import {
    BookingBadgeType,
    BookingScheduleSettings, BookingScheduleSettingsType,
    CookingTime,
    Menu, MenuBookingCategory, MenuBookingItem, MenuBookingItemData,
    MenuCategory,
    MenuCategoryWorkingSchedule,
    MenuCategoryWorkingScheduleDay,
    MenuItemWorkingSchedule,
    MenuItemWorkingScheduleDay,
} from "../../../../services/menu/models";
import {
    createBookingCategory, createMenuBookingItem,
     deleteBookingCategory,
     deleteMenuBookingItem,
    getBookingMenuById,
    getMenuById, updateBookingCategory,
    updateMenuBookingItem,
} from "../../../../services/menu";
import { locale } from "../../../../common/consts";

import { LocalizedString } from "../../../../services/general/models";
import BookingFormsStore, { BookingFormType } from "./forms";
import { ExecutionStatus } from "../../../../services/general/requestsResponses";
import {updateBookingOrderStatus} from "../../../../services/bookingOrders";
import {OrderStatus} from "../../../../services/orders/models";



const defaultBookingScheduleSettings: BookingScheduleSettings = {
    type: BookingScheduleSettingsType.withinDayByStep,
    step: 15,
    fromHour: 10,
    fromMinute: 0,
    toHour: 18,
    toMinute: 0
}

const defaultMenuCategoryWorkingScheduleDay: MenuCategoryWorkingScheduleDay = {
    isEnabled: false,
    slots: [
        {
            fromHour: 0,
            fromMinute: 0,
            toHour: 23,
            toMinute: 59,
        }
    ]
}

const defaultBadges = {
    [BookingBadgeType.new]: {
        isEnabled: false,
        type: BookingBadgeType.new
    },
    [BookingBadgeType.profitable]: {
        isEnabled: false,
        type: BookingBadgeType.profitable
    },
    [BookingBadgeType.hit]: {
        isEnabled: false,
        type: BookingBadgeType.hit
    },
};

const defaultMenuCategoryWorkingSchedule: MenuCategoryWorkingSchedule = {
    isEnabled: false,
    monday: defaultMenuCategoryWorkingScheduleDay,
    tuesday: defaultMenuCategoryWorkingScheduleDay,
    wednesday: defaultMenuCategoryWorkingScheduleDay,
    thursday: defaultMenuCategoryWorkingScheduleDay,
    friday: defaultMenuCategoryWorkingScheduleDay,
    saturday: defaultMenuCategoryWorkingScheduleDay,
    sunday: defaultMenuCategoryWorkingScheduleDay,
}
const defaultMenuItemWorkingScheduleDay: MenuItemWorkingScheduleDay = {
    isEnabled: false,
    slots: [
        {
            fromHour: 0,
            fromMinute: 0,
            toHour: 23,
            toMinute: 59,
        }
    ]
}

const defaultMenuItemWorkingSchedule: MenuItemWorkingSchedule = {
    isEnabled: false,
    monday: defaultMenuItemWorkingScheduleDay,
    tuesday: defaultMenuItemWorkingScheduleDay,
    wednesday: defaultMenuItemWorkingScheduleDay,
    thursday: defaultMenuItemWorkingScheduleDay,
    friday: defaultMenuItemWorkingScheduleDay,
    saturday: defaultMenuItemWorkingScheduleDay,
    sunday: defaultMenuItemWorkingScheduleDay,
}


class BookingMenuStore {
    forms = new BookingFormsStore();
    currentMenu?: {
        id: number,
        menuCategories: MenuBookingCategory[];
        menuItemsByCategoryId: { [s: string]: MenuBookingItem[] };
        menu: Menu
    };
    currentMenuItem?: MenuBookingItem;
    selectedCategory: number = 0;
    categoryWorkingSchedule: MenuCategoryWorkingSchedule = defaultMenuCategoryWorkingSchedule;
    itemWorkingSchedule: MenuItemWorkingSchedule = defaultMenuItemWorkingSchedule;
    categoryCookingTime: CookingTime = { fromTime: null, toTime: null };
    itemCookingTime: CookingTime = { fromTime: null, toTime: null };
    categoriesList: MenuCategory[] = [];
    newCategoryId: number | undefined = undefined;
    isLoaded: boolean = false;
    bookingScheduleSettings: BookingScheduleSettings = defaultBookingScheduleSettings;

    constructor() {
        makeAutoObservable(this);
    }

    @computed
    setBookingScheduleSettings() {
        this.bookingScheduleSettings = this.currentMenuItem?.data.bookingScheduleSettings;
    }


    async updateBookingOrderStatus (params: {bookingOrderId: number,  menuId: number, newStatus: OrderStatus} ) {
        const response = await updateBookingOrderStatus(params);

    }


    async getCategoriesByMenuId(menuId: number) {
        const response = await getMenuById({
            menuId: menuId
        });
        runInAction(() => {
            this.categoriesList = response.menuCategories;
        })
    }

    @action
    setIsLoaded(value: boolean) {
        this.isLoaded = value;
    }

    async fetchMenuById(menuId: number) {
        this.setIsLoaded(false);
        const response = await getBookingMenuById({
            menuId: menuId
        });

        runInAction(() => {
            this.currentMenu = {
                id: menuId,
                menuCategories: response.menuCategories.sort((item1, item2) => {
                    return item1.data.sortOrder - item2.data.sortOrder
                }),
                menuItemsByCategoryId: response.menuItemsByCategoryId,
                menu: response.menu
            }
        })
        this.setIsLoaded(true);
    }

    async reloadMenu() {
        if (!this.currentMenu) return;
        await this.fetchMenuById(this.currentMenu.id)
    }


    async createCategory(params: {
        name: string,
        sortOrder: number,
        isInStopList: boolean,
    }) {
        const res = await createBookingCategory({
            data: {
                name: {
                    [locale]: params.name
                },
                sortOrder: params.sortOrder,
                isInStopList: params.isInStopList,
            },
            menuId: this.currentMenu.id
        });
        runInAction(() => {
            this.newCategoryId = res.menuBookingCategoryId;
        })
        await this.reloadMenu();
    }

    async updateCategory(id: number, params: {
        name: LocalizedString,
        sortOrder: number,
        isInStopList: boolean,
    }, forceReload: boolean = true) {
        await updateBookingCategory({
            data: {
                name: params.name,
                sortOrder: params.sortOrder,
                isInStopList: params.isInStopList,
            },
            menuId: this.currentMenu.id,
            menuBookingCategoryId: id
        });
        if (forceReload) await this.reloadMenu();
    }

    async removeCategory(menuId: number, menuCategoryId: number) {
        await deleteBookingCategory({ menuId, menuBookingCategoryId: menuCategoryId });

        await this.reloadMenu();
    }

    async createMenuItem(params: MenuBookingItemData) {
        const res = await createMenuBookingItem({
            data: params,
            menuId: this.currentMenu.id,
        });
        await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished,
            menuItemId: res.menuBookingItemId
        }
    }

    async updateBookingItem(id: number, params: MenuBookingItemData, forceReload: boolean = true) {
        const res = await updateMenuBookingItem({
            data: params,
            menuId: this.currentMenu.id,
            menuBookingItemId: id
        });
        if (forceReload) await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished
        };
    }


    async removeMenuItem(menuId: number, menuBookingItemId: number) {
        const res = await deleteMenuBookingItem({
            menuBookingItemId, menuId
        });
        await this.reloadMenu();
        return {
            err: res.executionStatus !== ExecutionStatus.Finished
        };
    }

    /*async deleteMenu (menuId: number) {
        const res = await deleteMenu({menuId})
    }

    async deleteBookingMenu (menuId: number) {
        const res = await deleteBookingMenu({menuId})
    }*/


    @action setCategoryName(name: LocalizedString) {
        this.forms.forms[BookingFormType.createBookingCategory].current.data.name = name;
    }

    /*  @action setMenuItemName(name:LocalizedString) {
          this.currentMenuItem.data.name = name;
          this.updateMenuItem(this.currentMenuItem.id, {
              ...this.currentMenuItem.data,
              name: this.currentMenuItem.data.name
          }, false);
      }
      @action setMenuItemDescription(description:LocalizedString) {
          this.currentMenuItem.data.description = description;
          this.updateMenuItem(this.currentMenuItem.id, {
              ...this.currentMenuItem.data,
              description: this.currentMenuItem.data.description
          }, false);
      }
  */
    @action
    setCurrentMenuId(menuId: number) {
        this.currentMenu.id = menuId;
    }

    @action
    setSelectedCategory(category: number) {
        this.selectedCategory = category;
    }


    @computed
    getCategoryById(id: number) {
        return this.currentMenu.menuCategories.find(item => item.id === id);
    }


    @computed
    getMenuItemByCategoryIdAndId(categoryId: number, itemId: number): MenuBookingItem {
        const current = this.currentMenu.menuItemsByCategoryId[categoryId].find(item => item.id === itemId);
        return {
            ...current,
            data: {
                ...current.data,
                badges: {
                    ...defaultBadges,
                    ...current.data.badges
                }
            }
        }
    }

    @action
    reset() {
        this.currentMenu = undefined;
        this.forms = new BookingFormsStore();
        this.selectedCategory = 0;
        this.isLoaded = false;
    }

    @action
    resetNewCategory() {
        this.newCategoryId = undefined;
    }


    @action setMenuItemName(name: LocalizedString) {
        this.currentMenuItem.data.name = name;
        this.updateBookingItem(this.currentMenuItem.id, {
            ...this.currentMenuItem.data,
            name: this.currentMenuItem.data.name
        }, false);
    }

    @action setMenuItemDescription(description: LocalizedString) {
        this.currentMenuItem.data.description = description;
        this.updateBookingItem(this.currentMenuItem.id, {
            ...this.currentMenuItem.data,
            description: this.currentMenuItem.data.description
        }, false);
    }


    @action
    setMondayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            monday: workingSchedule
        }
    }

    @action
    setTuesdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            tuesday: workingSchedule

        }
    }

    @action
    setWednesdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            wednesday: workingSchedule

        }
    }

    @action
    setThursdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            thursday: workingSchedule

        }
    }

    @action
    setFridayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            friday: workingSchedule

        }
    }

    @action
    setSaturdayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            saturday: workingSchedule

        }
    }

    @action
    setSundayItemSchedule(workingSchedule: MenuItemWorkingScheduleDay) {
        this.itemWorkingSchedule = {
            ...this.itemWorkingSchedule,
            sunday: workingSchedule
        }
    }

    @action
    setMondayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            monday: workingSchedule
        }
    }

    @action
    setTuesdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            tuesday: workingSchedule

        }
    }

    @action
    setWednesdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            wednesday: workingSchedule

        }
    }

    @action
    setThursdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            thursday: workingSchedule

        }
    }

    @action
    setFridayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            friday: workingSchedule

        }
    }

    @action
    setSaturdayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            saturday: workingSchedule

        }
    }

    @action
    setSundayCategorySchedule(workingSchedule: MenuCategoryWorkingScheduleDay) {
        this.categoryWorkingSchedule = {
            ...this.categoryWorkingSchedule,
            sunday: workingSchedule

        }
    }

    @action
    setCategoryCookingTime(cookingTime: CookingTime) {
        this.categoryCookingTime = cookingTime;
    }

    @action
    setItemCookingTime(cookingTime: CookingTime) {
        this.itemCookingTime = cookingTime;
    }


    async toggleStopList(item: MenuBookingItem, value: boolean) {
        const res = await this.updateBookingItem(item.id, {
            ...item.data,
            isInStopList: value
        });
        return res.err
    }

    @action
    setScheduleItemStep(step: number) {
        this.bookingScheduleSettings.step = step;
    }

    @action
    setScheduleItemSlot(settings: BookingScheduleSettings) {
        this.bookingScheduleSettings = settings;
    }

    @action
    resetBookingScheduleSettings() {
        if (!this.currentMenuItem) return;
        this.currentMenuItem.data.bookingScheduleSettings = defaultBookingScheduleSettings;
    }


}

export default BookingMenuStore;
